<template>
  <v-dialog :model-value="show" max-width="600" @update:model-value="$emit('close')">
    <v-card>
      <v-card-title :class="isEditing ? 'bg-primary text-white' : 'bg-success text-white'">
        <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
        {{ isEditing ? 'Edit Mapping' : 'Add Mapping' }}
      </v-card-title>
      <v-card-text class="pt-4">
        <v-form ref="mappingForm">
          <v-autocomplete
            v-model="localFormData.distributor_id"
            :items="distributors"
            item-title="name"
            item-value="id"
            label="Distributor"
            :rules="[v => !!v || 'Distributor is required']"
            dense
            outlined
            class="mb-3"
            :disabled="isEditing"
            hint="Select the distributor - this will filter available services and products"
            persistent-hint
            @update:model-value="onDistributorChange"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <v-list-item-subtitle>ID: {{ item.raw.id }}</v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="localFormData.service_id"
            :items="distributorServices"
            item-title="name"
            item-value="id"
            label="Service"
            :rules="[v => !!v || 'Service is required']"
            dense
            outlined
            class="mb-3"
            :disabled="isEditing || !localFormData.distributor_id"
            :loading="loadingServices"
            hint="Select the service to map (filtered by distributor's hierarchy)"
            persistent-hint
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <v-list-item-subtitle>
                  {{ item.raw.service_type_name || '' }} - ID: {{ item.raw.id }}
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="localFormData.product_id"
            :items="distributorProducts"
            item-title="display_name"
            item-value="id"
            label="Billing Product"
            :rules="[v => !!v || 'Product is required']"
            dense
            outlined
            class="mb-3"
            :disabled="!localFormData.distributor_id"
            :loading="loadingProducts"
            hint="Select the billing product (filtered by distributor's available products)"
            persistent-hint
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <v-list-item-subtitle>
                  <v-chip size="x-small" color="primary" class="mr-1">i_product: {{ item.raw.billing_i_product }}</v-chip>
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="localFormData.mvno_id"
            :items="mvnoOptions"
            item-title="name"
            item-value="id"
            label="MVNO"
            dense
            outlined
            class="mb-3"
            :loading="loadingMvnos"
            hint="Select 'All' to apply to any MVNO, or select a specific MVNO"
            persistent-hint
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props">
                <v-list-item-subtitle v-if="item.raw.id">
                  ID: {{ item.raw.id }}
                </v-list-item-subtitle>
              </v-list-item>
            </template>
            <template #append-inner>
              <v-chip v-if="selectedServiceMvnoId" size="x-small" color="info">
                Service MVNO: {{ selectedServiceMvnoId }}
              </v-chip>
            </template>
          </v-autocomplete>

          <v-alert v-if="!localFormData.distributor_id" type="warning" density="compact" class="mb-3">
            Select a distributor first to see available services and products
          </v-alert>

          <v-switch
            v-model="localFormData.active"
            label="Active"
            color="success"
            hint="Only active mappings are used"
            persistent-hint
            density="compact"
          >
            <template v-slot:label>
              <span class="d-flex align-center">
                <v-icon class="mr-2" :color="localFormData.active ? 'success' : 'error'">
                  {{ localFormData.active ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
                {{ localFormData.active ? 'Active' : 'Inactive' }}
              </span>
            </template>
          </v-switch>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="$emit('close')">Cancel</v-btn>
        <v-btn
          :color="isEditing ? 'primary' : 'success'"
          @click="save"
          :loading="saving"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import {
  getServicesForDistributor,
  getProductsForDistributor,
  getMvnosForSelect
} from "@/composables/useDistributorServiceProductsAPI";
import { log } from "@/composables/useLog";

export default defineComponent({
  name: 'DistributorServiceProductDialog',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    formData: {
      type: Object,
      default: () => ({
        id: null,
        service_id: null,
        distributor_id: null,
        product_id: null,
        mvno_id: null,
        active: true
      })
    },
    distributors: {
      type: Array,
      default: () => []
    },
    token: {
      type: String,
      required: true
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'save', 'showSnackbar'],
  data() {
    return {
      localFormData: { ...this.formData },
      distributorServices: [],
      distributorProducts: [],
      mvnos: [],
      loadingServices: false,
      loadingProducts: false,
      loadingMvnos: false
    }
  },
  computed: {
    selectedServiceMvnoId() {
      if (!this.localFormData.service_id) return null;
      const service = this.distributorServices.find(s => s.id === this.localFormData.service_id);
      return service?.mvno_id || null;
    },
    mvnoOptions() {
      return [{ id: null, name: 'All (any MVNO)' }, ...this.mvnos];
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.localFormData = { ...this.formData };
        this.loadMvnos();
        if (this.formData.distributor_id) {
          this.loadDistributorData(this.formData.distributor_id);
        } else {
          this.distributorServices = [];
          this.distributorProducts = [];
        }
      }
    },
    formData: {
      handler(newVal) {
        this.localFormData = { ...newVal };
      },
      deep: true
    }
  },
  methods: {
    async loadMvnos() {
      if (this.mvnos.length > 0) return; // Already loaded
      this.loadingMvnos = true;
      try {
        const result = await getMvnosForSelect(this.token);
        if (!result.error) {
          this.mvnos = result.mvnos || [];
        }
      } catch (err) {
        log("Error loading MVNOs:", err);
      } finally {
        this.loadingMvnos = false;
      }
    },

    async onDistributorChange(distributorId) {
      log("Distributor changed to:", distributorId);
      // Reset selections when distributor changes (not during edit)
      if (!this.isEditing) {
        this.localFormData.service_id = null;
        this.localFormData.product_id = null;
      }
      this.distributorServices = [];
      this.distributorProducts = [];

      if (!distributorId) return;

      await this.loadDistributorData(distributorId);

      // Show warnings if no data
      if (this.distributorServices.length === 0) {
        this.$emit('showSnackbar', { message: 'No services available for this distributor', color: 'warning' });
      }
      if (this.distributorProducts.length === 0) {
        this.$emit('showSnackbar', { message: 'No products assigned to this distributor', color: 'warning' });
      }
    },

    async loadDistributorData(distributorId) {
      if (!distributorId) return;

      this.loadingServices = true;
      this.loadingProducts = true;

      try {
        const [servicesResult, productsResult] = await Promise.all([
          getServicesForDistributor(this.token, distributorId),
          getProductsForDistributor(this.token, distributorId)
        ]);

        if (!servicesResult.error) {
          this.distributorServices = servicesResult.services || [];
        }

        if (!productsResult.error) {
          this.distributorProducts = productsResult.products || [];
        }
      } catch (err) {
        log("Error loading distributor data:", err);
      } finally {
        this.loadingServices = false;
        this.loadingProducts = false;
      }
    },

    save() {
      if (!this.localFormData.service_id || !this.localFormData.distributor_id || !this.localFormData.product_id) {
        this.$emit('showSnackbar', { message: 'Please fill all required fields', color: 'error' });
        return;
      }
      this.$emit('save', { ...this.localFormData });
    }
  }
});
</script>
